import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { PageHeader } from '@/components/Common';
import { ThresholdFields } from '@/components/Threshold';
import { TEST_IDS } from '@/constants/ids';
import {
  ShieldWarningIcon,
  ClipboardTextIcon,
  StackPlusIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPES = [
  {
    key: 'walkthrough',
    title: 'Gemba Walk',
    desc: 'On-site hazard observation, CFR-backed findings, photos, and corrective actions.',
    icon: ShieldWarningIcon,
    testId: TEST_IDS.engTypeWalkthrough,
  },
  {
    key: 'gap_check',
    title: 'Doc Review Only',
    desc: '53-item OSHA documentation check + per-document review across 9 program areas.',
    icon: ClipboardTextIcon,
    testId: TEST_IDS.engTypeGapCheck,
  },
  {
    key: 'combined',
    title: 'Combined',
    desc: 'Gemba Walk + Doc Review under one facility record. One report covers both.',
    icon: StackPlusIcon,
    testId: TEST_IDS.engTypeCombined,
  },
];

export default function NewEngagementPage() {
  const nav = useNavigate();
  const [type, setType] = useState('walkthrough');
  const [hasFleet, setHasFleet] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    site_address: '',
    site_address_note: '',
    industry: '',
    visit_date: '',
    notes: '',
    employee_count: '',
    industry_type: '',
  });
  const [busy, setBusy] = useState(false);

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    if (!form.employee_count || Number(form.employee_count) < 1) {
      toast.error('Employee count is required (must be 1 or greater).');
      return;
    }
    if (!form.industry_type) {
      toast.error('Industry type is required.');
      return;
    }
    setBusy(true);
    try {
      const payload = { type, has_fleet: hasFleet, ...form, employee_count: Number(form.employee_count) };
      const { data } = await api.post('/engagements', payload);
      toast.success('Engagement created');
      nav(`/engagements/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Step 1 of 1"
        title="New Engagement"
        subtitle="Pick the engagement type, then capture the facility intake. You can edit anything later."
      />

      <form className="px-5 md:px-8 pb-12 flex flex-col gap-6 max-w-3xl" onSubmit={submit}>
        {/* Type select */}
        <div>
          <div className="gl-label">Engagement Type</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TYPES.map((t) => {
              const Icon = t.icon;
              const active = type === t.key;
              return (
                <button
                  type="button"
                  key={t.key}
                  onClick={() => setType(t.key)}
                  className="text-left p-4 border-2 rounded-sm transition-all"
                  style={{
                    background: active ? 'var(--gl-dark)' : '#fff',
                    color: active ? '#fff' : 'var(--gl-dark)',
                    borderColor: active ? 'var(--gl-dark)' : '#e5e7eb',
                  }}
                  data-testid={t.testId}
                >
                  <Icon size={28} weight="fill" color={active ? '#C9A84C' : '#2A52A0'} />
                  <div
                    className="mt-2 font-display font-extrabold uppercase tracking-wide text-lg leading-tight"
                    style={{ letterSpacing: '0.04em' }}
                  >
                    {t.title}
                  </div>
                  <div className="mt-1 text-xs" style={{ color: active ? '#cbd5e1' : 'var(--gl-muted)' }}>
                    {t.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(type === 'gap_check' || type === 'combined') && (
            <div className="md:col-span-2">
              <div className="gl-label">Commercial Motor Vehicle Operations</div>
              <label
                className="flex items-start gap-3 p-3 border-2 rounded-sm cursor-pointer transition-colors"
                style={{
                  borderColor: hasFleet ? 'var(--gl-navy)' : '#d1d5db',
                  background: hasFleet ? '#eff4fb' : '#fff',
                }}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5"
                  checked={hasFleet}
                  onChange={(e) => setHasFleet(e.target.checked)}
                  data-testid="eng-has-fleet"
                />
                <div>
                  <div className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}>
                    Facility operates commercial motor vehicles (FMCSA)
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
                    Check this to include the 9-item Fleet / FMCSA category in the gap checklist (49 CFR — DQ files, DVIRs, ELD, BMC-91, etc.). Leave unchecked to skip Fleet entirely (it won't count against the compliance score).
                  </div>
                </div>
              </label>
            </div>
          )}
          <Field label="Business Name *" testid={TEST_IDS.engBusinessName}>
            <input
              className="gl-input"
              value={form.business_name}
              onChange={(e) => up('business_name', e.target.value)}
              placeholder="Acme Logistics"
              required
              data-testid={TEST_IDS.engBusinessName}
            />
          </Field>
          <Field label="Industry">
            <input
              className="gl-input"
              value={form.industry}
              onChange={(e) => up('industry', e.target.value)}
              placeholder="Warehousing & Distribution"
              data-testid={TEST_IDS.engIndustry}
            />
          </Field>
          <ThresholdFields
            employeeCount={form.employee_count}
            industryType={form.industry_type}
            onChange={up}
            required
          />
          <Field label="Contact Name">
            <input
              className="gl-input"
              value={form.contact_name}
              onChange={(e) => up('contact_name', e.target.value)}
              placeholder="Operations Manager"
              data-testid={TEST_IDS.engContactName}
            />
          </Field>
          <Field label="Contact Email">
            <input
              className="gl-input"
              type="email"
              value={form.contact_email}
              onChange={(e) => up('contact_email', e.target.value)}
              placeholder="ops@acme.com"
              data-testid={TEST_IDS.engContactEmail}
            />
          </Field>
          <Field label="Contact Phone">
            <input
              className="gl-input"
              value={form.contact_phone}
              onChange={(e) => up('contact_phone', e.target.value)}
              placeholder="(555) 123-4567"
              data-testid={TEST_IDS.engContactPhone}
            />
          </Field>
          <Field label="Visit Date">
            <input
              className="gl-input"
              type="date"
              value={form.visit_date}
              onChange={(e) => up('visit_date', e.target.value)}
              data-testid={TEST_IDS.engVisitDate}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Site Address">
              <input
                className="gl-input"
                value={form.site_address}
                onChange={(e) => up('site_address', e.target.value)}
                placeholder="1234 Industrial Pkwy, Dayton, OH"
                data-testid={TEST_IDS.engSiteAddress}
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Address Note (optional)">
              <input
                className="gl-input"
                value={form.site_address_note}
                onChange={(e) => up('site_address_note', e.target.value)}
                placeholder="Gate code 4592, use back entrance"
                data-testid="eng-site-address-note"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea
                className="gl-input gl-textarea"
                value={form.notes}
                onChange={(e) => up('notes', e.target.value)}
                rows={3}
                placeholder="Context, scope, special instructions…"
                data-testid={TEST_IDS.engNotes}
              />
            </Field>
          </div>
        </div>

        <div className="flex gap-3 mt-2">
          <button
            type="submit"
            className="gl-btn gl-btn-primary"
            disabled={busy}
            data-testid={TEST_IDS.engSubmit}
          >
            {busy ? 'Creating…' : 'Create Engagement'}
          </button>
          <button type="button" className="gl-btn gl-btn-secondary" onClick={() => nav(-1)}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="gl-label">{label}</div>
      {children}
    </div>
  );
}
